import React, { useCallback, useRef, useState } from "react";
import MarkdownEditor from "components/markdown-editor/MarkdownDocument";
import { Button, MenuList, Stack } from "@mui/material";
import { colors } from "colors";
import useIsMobile from "hooks/react-lib/generic-ui/useIsMobile";
import LabelledOutline from "components/react-lib/generic-ui/LabelledOutline";
import CreateMDDocumentButton from "components/markdown-editor/CreateMDDocumentButton";
import ActiveDocument from "components/markdown-editor/ActiveDocument";
import DocumentList from "components/markdown-editor/DocumentList";
import MainMenu from "components/menus/MainMenu";
import CurrentBotHeader from "components/bot/CurrentBotHeader";
import { useAuth } from "context/react-lib/firebase-auth/AuthProvider";
import ChargeAccountDisplay from "components/react-lib/stripe-credit-payments/ChargeAccountDisplay";
import { useCollection } from "react-firebase-hooks/firestore";
import { addDoc, collection, doc, orderBy, query, updateDoc, where } from "firebase/firestore";
import { db } from "firebaseApp";
import DocumentDrawerMenu from "components/menus/DocumentDrawerMenu";
import EditDocumentModal from "components/react-lib/generic-ui/modals/EditDocumentModal";
import { useDispatch, useSelector } from "react-redux";
import { modalsActions } from "store/react-lib/generic-ui/modals";
import { COLLECTIONS } from "firebaseCollections";
import AttachFileModal from "components/attachments/AttachFileModal";
import AttachmentList from "components/files/AttachmentList";
import View from "./View";
import DocumentsLoader from "components/data/DocumentsLoader";
import { MODALS } from "modals";
import { editorActions } from "store/editor";

const DocumentEditorView = () => {

  const dispatch = useDispatch();
  const auth = useAuth();
  const uid = auth.currentUser.uid;

  const botId = useSelector((state) => state.bot?.current);

  const attachmentTarget = useSelector(
    (state) => state.editor.attachmentTarget
  );

  const activeDocumentId = useSelector(
    (state) => state.editor.activeDocument?.id
  );

  const editorState = useSelector((state) => state.editor);

  const DOCUMENT_LIST_WIDTH = 300;
  const isMobile = useIsMobile();

  const processAttachment = async ({ fileId, fileData }) => {
    try {
      const payload = {
        ...fileData,
        fileId,
        uid,
        targetId: attachmentTarget, // This will always be the latest value
        createdAt: new Date().getTime(),
      };
      await addDoc(collection(db, COLLECTIONS.attachments), payload);
      dispatch(modalsActions.closeModal("attachFileToDocumentModal"));
    } catch (error) {
      console.log(error);
    } finally {
      // cleanup or additional actions if needed
    }
  };

  const handelSave = async () => {
    if (editorState.activeDocument) {
      await updateDoc(
        doc(db, COLLECTIONS.mdDocuments, editorState.activeDocument.id),
        {
          content: editorState.activeDocument.content,
        }
      );
    }
  };


  return (
    <View
      renderLeftActions={() => {
        return <CurrentBotHeader />;
      }}
      renderRightActions={() => {
        return <DocumentDrawerMenu />;
      }}
    >
      <DocumentsLoader />
      <AttachFileModal
        name={MODALS.attachFileToDocumentModal}
        successCallback={processAttachment}
      />
      <EditDocumentModal
        name={MODALS.createDocumentModal}
        title="Create Document"
        documentRef={collection(db, "mdDocuments")}
        isCreate={true}
        fieldsToEdit={["name", "type"]}
        createExtraData={{
          uid,
        }}
        fieldConfig={{
          name: { props: { color: "secondary" } },
          type: { props: { color: "secondary" } },
        }}
        saveCallback={(doc) => dispatch(editorActions.setActiveDocument(doc))}
      />
      {!isMobile && (
        <Stack p={1}>
          <Stack px={2} pb={1}>
            <Button
              color="primary"
              variant="contained"
              onClick={() =>
                dispatch(modalsActions.openModal(MODALS.createDocumentModal))
              }
            >
              New Document
            </Button>
          </Stack>
          <MenuList
            sx={{
              height: "95%",
              width: `${DOCUMENT_LIST_WIDTH}px`,
              overflowY: "auto",
              overflowX: "none",
              boxSizing: "border-box",
            }}
          >
            <DocumentList handelSave={handelSave}/>
          </MenuList>
        </Stack>
      )}
      <Stack
        sx={{
          width: `calc(100% - ${isMobile ? 0 : DOCUMENT_LIST_WIDTH}px)`,
          height: "100%",
        }}
      >
        <AttachmentList label="Bot attachments" targetId={botId} />
        <AttachmentList
          label="Document attachments"
          targetId={activeDocumentId}
        />
        <ActiveDocument />
      </Stack>
    </View>
  );
};

export default DocumentEditorView;
